import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ModalActionsButtons } from "@/components/modal-actions-buttons";

type AddingMode = "one" | "list";

const NameConfigAddModal = ({
  //NICKNAME
  open,
  title,
  confirmLoading = false,
  onCancel,
  onSave,
}: {
  open: boolean;
  title?: string;
  confirmLoading?: boolean;
  onCancel?: () => void;
  onSave?: (value: string[]) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const [addingMode, setAddingMode] = useState<AddingMode>("one");

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm();

  function handleChangeAddingMode(event: React.ChangeEvent<HTMLInputElement>) {
    setAddingMode(event.target.value as AddingMode);
  }

  const onSubmit = (data: any) => {
    const values: string[] = data[addingMode].trim().split("\n");
    values.forEach((value) => validateNotEmpty(value));
    if (onSave) onSave(values);
  };

  function validateNotEmpty(value: string) {
    if (!value || !value.trim()) {
      alert(t("namesRequired"));
      return false;
    }
    return true;
  }

  return (
    <Dialog
      open={open}
      key="nickname-create-modal"
      maxWidth="lg"
      onClose={(event, reason) => {
        if (
          reason === "backdropClick" &&
          isDirty &&
          !window.confirm("Data has been changed. Do you want discard changes?")
        )
          return; //ask question if changes==true and click outSide
        onCancel?.();
      }}
    >
      <DialogTitle>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => onCancel && onCancel()}
          style={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Divider sx={{ marginBottom: 2 }} />

        <form onSubmit={handleSubmit(onSubmit)}>
          <Typography variant="body1">{t("chooseAddingType")}</Typography>
          <RadioGroup
            value={addingMode}
            onChange={handleChangeAddingMode}
            row
            sx={{ marginBottom: 2 }}
          >
            <FormControlLabel value="one" control={<Radio />} label={t("one")} />
            <FormControlLabel value="list" control={<Radio />} label={t("list")} />
          </RadioGroup>

          <Divider sx={{ marginY: 2 }} />

          {addingMode === "one" && (
            <Controller
              name="one"
              control={control}
              rules={{ required: t("namesRequired") }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t("name")}
                  variant="outlined"
                  fullWidth
                  error={!!error?.message}
                  helperText={error?.message}
                  margin="normal"
                />
              )}
            />
          )}

          {addingMode === "list" && (
            <Controller
              name="list"
              control={control}
              rules={{ required: t("namesRequired") }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label={t("names")}
                  variant="outlined"
                  multiline
                  rows={10}
                  fullWidth
                  error={!!error?.message}
                  helperText={error?.message}
                  margin="normal"
                />
              )}
            />
          )}

          <ModalActionsButtons
            onCancelClick={() => onCancel && onCancel()}
            onConfirmClick={() => void 0}
            loading={confirmLoading}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NameConfigAddModal;
